import { BaseResourceStore, TInitialState } from "_common/resources/BaseResourceStore";
import { GEO_ZONE_TYPE, TGeoZoneMdl } from "geoZones/_models/GeoZoneMdl";
import { fetchUtils } from "_common/_utils/fetchUtils";
import { action, computed, extendObservable, observable, toJS } from "mobx";
import { LoadingStateMdl } from "_common/loaders/_models/LoadingStateMdl";
import { propertiesStore } from "properties/_stores/propertiesStore";
import {
    getResourceInitialStateValue,
    putItemsInInitialState,
    putObjectInInitialState,
    putPromiseResultInInitialState,
} from "_common/_utils/initialStateUtils";
import { removeAccentFromString } from "_common/_utils/alphaNumUtils";
import _ from "lodash";
import { TFilterType } from "admin/_common/resources/ResourceFilterMdl";
import { TFilter } from "admin/_common/filters/TFilter";
import slugify from "slugify";
import { WithRequiredProperty } from "_common/types/GenericTypes";
import i18next from "i18next";

export class GeoZonesStore extends BaseResourceStore<WithRequiredProperty<TGeoZoneMdl, "_id">> {
    @observable geoZone: TGeoZoneMdl | undefined;
    @observable geoZoneState: LoadingStateMdl<
        TGeoZoneMdl | Omit<TGeoZoneMdl, "localized"> | undefined
    > = new LoadingStateMdl<TGeoZoneMdl | Omit<TGeoZoneMdl, "localized"> | undefined>("IDLE");

    @observable search = "";
    @observable opened = false;
    @observable selectedIndex = -1;
    @observable geoZones: TGeoZoneMdl[] = [];
    @observable provinces: TGeoZoneMdl[] = [];
    @observable children: { [key: string]: Pick<TGeoZoneMdl, "type" | "address" | "name">[] } = {};
    @observable parents: { [key: string]: TGeoZoneMdl[] } = {};
    @observable parentStates: { [key: string]: LoadingStateMdl } = {};
    @observable unlinkGeoZone: TGeoZoneMdl | undefined = undefined;
    @observable geoZonesState: LoadingStateMdl<TGeoZoneMdl[]> = new LoadingStateMdl<TGeoZoneMdl[]>();
    @observable unlinkGeoZonesState: LoadingStateMdl<TGeoZoneMdl> = new LoadingStateMdl<TGeoZoneMdl>();
    @action setSearch = _.debounce((s: string) => {
        this.open(s.length > 0);
        this.selectedIndex = -1;
        this.search = s;
        this.fetchGeozones(slugify(s), 5);
        propertiesStore.fetchPropertiesForSearch(removeAccentFromString(s));
    }, 400);

    constructor() {
        super("geoZones");
        this.onInit();
    }

    @computed get address() {
        return {
            city: this.geoZone?.address?.city ?? undefined,
            province: this.geoZone?.address?.province ?? undefined,
            neighbourhood: this.geoZone?.address?.neighbourhood ?? undefined,
        };
    }

    @computed get zoneName() {
        return (
            this.geoZone?.name.split(",")?.[0] || this.geoZone?.address?.neighbourhood || this.geoZone?.address?.city
        );
    }

    @computed get results() {
        return {
            geoZones: this.geoZones.sort((a, b) => a.name.length - b.name.length),
        };
    }

    @computed get selectedResult() {
        return this.results.geoZones[this.selectedIndex];
    }

    @computed get isSearching() {
        return this.opened && this.search.length > 1;
    }

    @action open(opened: boolean) {
        this.opened = opened;
    }

    @action pressArrowDown() {
        if (this.results.geoZones) {
            if (this.selectedIndex === this.results.geoZones.length - 1) {
                this.selectedIndex = 0;
            } else {
                this.selectedIndex += 1;
            }
        }
    }

    @action pressArrowUp() {
        if (this.results.geoZones) {
            if (this.selectedIndex <= 0) {
                this.selectedIndex = this.results.geoZones.length - 1;
            } else {
                this.selectedIndex -= 1;
            }
        }
    }

    @action setSelectedIndex(index: number) {
        this.selectedIndex = index;
    }

    @action resetGeoZone() {
        this.geoZone = undefined;
        this.geoZoneState.setStatus("IDLE");
    }

    @action setGeoZone(geoZone: TGeoZoneMdl) {
        this.geoZone = geoZone;
    }

    fetchGeozones(value?: string, limit = 10, regionOnly = false) {
        if (!this.geoZonesState.isLoading) {
            this.geoZonesState.startLoading();
            const filters: TFilter[] = [];
            if (value) {
                filters.push({ id: "slug", type: TFilterType.STRING, value });
            }
            if (regionOnly) {
                filters.push({
                    id: "isRegion",
                    type: TFilterType.BOOLEAN,
                    value: true,
                });
            }

            const filtersParam = filters.length > 0 ? `&filters=${JSON.stringify(filters)}` : "";
            const url = `${this.apiPath}/listing?limit=${limit}${filtersParam}`;
            return fetchUtils.get<{ items: TGeoZoneMdl[] }>(url).then(
                action(({ data: { items } }) => {
                    this.geoZones = items;
                    this.geoZonesState.setSuccess(items);
                }),
            );
        }
    }

    async fetchAllGeoZones(limit = Number.MAX_SAFE_INTEGER) {
        const url = `${this.apiPath}/listing?limit=${limit}`;
        return fetchUtils.get<{ items: TGeoZoneMdl[] }>(url);
    }

    fetchProvinces() {
        if (this.provinces.length > 0) return Promise.resolve(this.provinces);
        const filters: TFilter[] = [{ id: "type", type: TFilterType.STRING, value: GEO_ZONE_TYPE.PROVINCE }];
        return putPromiseResultInInitialState(
            "provinces",
            fetchUtils
                .get<{ items: TGeoZoneMdl[] }>(`${this.apiPath}/listing?limit=100&filters=${JSON.stringify(filters)}`)
                .then(
                    action(({ data: { items } }) => {
                        putItemsInInitialState<TGeoZoneMdl>("provinces", items);
                        this.provinces = items;
                        return items;
                    }),
                ),
        );
    }

    fetchChildrenGeozone(parentId: string) {
        if (toJS(this.children?.[parentId])) {
            return Promise.resolve(this.children[parentId]);
        }
        return putPromiseResultInInitialState(
            "children",
            fetchUtils
                .get<{ items: TGeoZoneMdl[] }>(`${this.apiPath}/children/${parentId}?lang=${i18next.language}`)
                .then(
                    action(({ data: { items } }) => {
                        this.children = { ...this.children, [parentId]: items };
                        putObjectInInitialState("children", { [parentId]: items });
                        extendObservable(this.children, { [parentId]: items });
                        return this.children;
                    }),
                ),
        );
    }

    // TODO description de "Parents"
    fetchParentGeoZones(itemId: string) {
        if (!this.parentStates[itemId]) {
            this.parentStates[itemId] = new LoadingStateMdl();
        } else if (this.parentStates[itemId].isSucceeded) {
            return this.parentStates[itemId];
        }
        this.parentStates[itemId].startLoading();
        putPromiseResultInInitialState(
            "parents",
            fetchUtils.get<{ items: TGeoZoneMdl[] }>(`${this.apiPath}/parents/${itemId}?lang=${i18next.language}`).then(
                action(({ data: { items } }) => {
                    this.parents = { ...this.parents, [itemId]: items };
                    this.parentStates[itemId].setSuccess();
                    putObjectInInitialState("parents", { [itemId]: items });
                    extendObservable(this.parents, { [itemId]: items });
                    return this.parents;
                }),
            ),
        );
        return this.parentStates[itemId];
    }

    fetchRegionOfProperty(propertyId: string) {
        return fetchUtils.get<TGeoZoneMdl>(`${this.apiPath}/regionOfProperty/${propertyId}`);
    }

    listing(
        offset = 0,
        limit?: number,
        _listId?: string,
        sort?: { [key: string]: number },
        filters?: TFilter[],
        _isShuffle = false,
    ) {
        const sortParam = sort ? `&sort=${JSON.stringify(sort)}` : "";
        let filtersParam = "";
        if (filters) filtersParam = filters.length > 0 ? `&filters=${JSON.stringify(filters)}` : "";
        const url = `${this.apiPath}/listing?offset=${offset}&limit=${limit}${sortParam}${filtersParam}`;
        const promise = fetchUtils
            .get<{ count: number; items: TGeoZoneMdl[] }>(url)
            .then(({ data: { count, items } }) => ({
                count,
                items: items.map((item) => {
                    const reformattedItem = this.reformatItem(item);
                    this.putItemInCache(reformattedItem);
                    return reformattedItem;
                }),
            }));
        return promise;
    }

    protected onInit(_fromRootCtor?: boolean) {
        this.geoZoneState.startLoading();

        const initialState = getResourceInitialStateValue("geoZone") as
            | TInitialState<WithRequiredProperty<TGeoZoneMdl, "_id">>
            | undefined;
        if (initialState) {
            this.geoZone = initialState.items[0];
            this.geoZoneState.setSuccess(this.geoZone);
        } else this.geoZoneState.setStatus("IDLE");

        const provincesInitialState = getResourceInitialStateValue("provinces") as
            | TInitialState<WithRequiredProperty<TGeoZoneMdl, "_id">>
            | undefined;

        if (provincesInitialState) {
            this.provinces = provincesInitialState.items;
        }

        const parentsInitialState = getResourceInitialStateValue("parents");

        if (parentsInitialState) {
            this.parents = parentsInitialState;
            for (const itemId of Object.keys(parentsInitialState)) {
                this.parentStates[itemId] = new LoadingStateMdl("SUCCEEDED");
            }
        }

        const childrenInitialState = getResourceInitialStateValue("children");

        if (childrenInitialState) {
            this.children = childrenInitialState;
        }
    }

    protected onReset() {
        this.geoZoneState = new LoadingStateMdl();
        this.geoZone = undefined;
        this.geoZonesState = new LoadingStateMdl();
        this.geoZones = [];
        this.provinces = [];
        this.children = {};
        this.parents = {};
        super.onReset();
    }
}

export const geoZonesStore = new GeoZonesStore();
